import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Activity, CreditCard, Gauge, PlugZap, Receipt, Users } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger, TooltipProvider } from '@ncoder/ui';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import { useOrgInvitations, useOrgMembers, useOrgRoles } from '@/hooks/org-admin';
import { OverviewPanel } from './OverviewPanel';
import { PeoplePanel } from './PeoplePanel';
import { McpServersPanel } from './McpServersPanel';
import { BillingPanel } from './BillingPanel';
import { UsagePanel } from './UsagePanel';
import { ObservabilityPanel } from './ObservabilityPanel';
import { ConnectionScopeLine } from '@/components/molecules/org-admin/ConnectionScope';
import {
  ORG_ADMIN_TABS,
  adminLocationParams,
  isLegacyTabParam,
  resolveAdminLocation,
} from '@/components/molecules/org-admin/adminTabs';
import type {
  OrgAdminPeopleSection,
  OrgAdminSelfContext,
  OrgAdminTab,
} from '@/components/molecules/org-admin/types';
import type { ManagementLevel } from '@/services/org-admin';

/**
 * Six destinations, grouped by the question the admin arrived with.
 *
 * The panel had seven peer tabs. Three of them (Overview, Usage, Analysis) read the same two
 * backends and rendered per-member spend and per-service cost three times each under six titles;
 * three more (Members, Roles, Invitations) were three views of one object graph, two of which are
 * setup-time screens. Ordering by frequency inside a flat list could not fix that, because the
 * problem was the list.
 *
 *   Overview        is anything wrong, and was this worth it   — the landing answer
 *   People          who is here and what may they use          — Members · Roles · Invitations
 *   MCP servers     which outside tools members can connect    — an org-admin catalog the gateway owns
 *   Billing         money IN: plans, card, credit, invoices
 *   Usage           money OUT: one per-member and one per-feature surface
 *   Observability   is it healthy: agent, tool and service reliability
 *
 * Billing before Usage keeps the money story in causal order — where it enters, then where it goes —
 * which was the one thing the old ordering got right. Observability sits after both, from the
 * sidebar's Delivery group at product's request: it is the tab an admin opens when something is
 * wrong rather than on a schedule, and inserting it earlier would split Billing from Usage.
 *
 * `adminTabs.ts` owns the tab list and the compatibility map for the four `?tab=` values that no
 * longer name a destination. That map is not optional: the active tab lives in the URL precisely so
 * a link can be shared, and without it every existing bookmark would land on Overview.
 */

const TAB_META: Record<OrgAdminTab, { label: string; icon: typeof Users }> = {
  overview: { label: 'Overview', icon: Gauge },
  people: { label: 'People', icon: Users },
  mcp: { label: 'MCP servers', icon: PlugZap },
  billing: { label: 'Billing', icon: CreditCard },
  usage: { label: 'Usage', icon: Receipt },
  // `Activity` rather than the `BarChart3` the sidebar row used: three tabs here already carry a
  // chart-shaped glyph, and a pulse reads as health where a bar chart reads as another report.
  observability: { label: 'Observability', icon: Activity },
};

export function OrgAdminLayout() {
  /**
   * The location lives in the URL: `?tab=` and, inside People, `?section=`.
   *
   * It used to be `useState`, which meant no deep link to a colleague ("look at the invitations
   * tab"), a refresh dropped you back to the first tab, and the back button left the page entirely.
   * `replace` keeps tab switching out of the history stack as individual entries while still making
   * the current screen addressable.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const location = resolveAdminLocation(tabParam, searchParams.get('section'));

  const setLocation = useCallback(
    (next: { tab: OrgAdminTab; section: OrgAdminPeopleSection }, replace = true) => {
      const params = new URLSearchParams(searchParams);
      const canonical = adminLocationParams(next);
      params.set('tab', canonical.tab);
      if (canonical.section) params.set('section', canonical.section);
      else params.delete('section');
      setSearchParams(params, { replace });
    },
    [searchParams, setSearchParams],
  );

  /*
   * Rewrite a pre-restructure URL to its canonical form, once.
   *
   * `?tab=roles` still RESOLVES correctly through the legacy map, so the link works without this.
   * Rewriting it means the address bar, and anything the admin copies out of it from now on, names
   * a destination that still exists. Guarded on `isLegacyTabParam` so it fires only for the retired
   * values and cannot loop.
   */
  useEffect(() => {
    if (isLegacyTabParam(tabParam)) setLocation(location);
  }, [tabParam, location, setLocation]);

  const setActiveTab = useCallback(
    (value: string) => {
      const next = resolveAdminLocation(value, searchParams.get('section'));
      setLocation(next);
    },
    [searchParams, setLocation],
  );

  const setPeopleSection = useCallback(
    (section: OrgAdminPeopleSection) => setLocation({ tab: 'people', section }),
    [setLocation],
  );

  const { data: members } = useOrgMembers();
  const { data: roles } = useOrgRoles();
  const { data: invitations } = useOrgInvitations();
  /*
   * The org rollup was read here only for the cycle-spend figure in the header, which product asked
   * to remove. The query went with it rather than being left to fetch a response nothing renders —
   * the Usage and Billing tabs each request it themselves when they need it.
   */

  const authUserId = useAuthStoreV1((s) => s.user?.id ?? null);
  // GraphQL enum casing — 'OWNER' | 'ADMIN' | 'MEMBER'. The same value the sidebar gates on.
  const orgRole = useAuthStoreV1((s) => s.selectedOrg?.role ?? null);
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);

  const self: OrgAdminSelfContext = useMemo(() => {
    const me = members?.find((m) => m.user_id === authUserId) ?? null;
    /*
     * Authority comes from the actor's own row when it is available, and falls back to the org role
     * already on the auth store otherwise.
     *
     * Without the fallback, authority was `null` for the entire time the members request was in
     * flight — so every primary action on the page ("Invite member", "New role") rendered disabled
     * on first paint and then enabled itself a moment later. It was also permanently null in the
     * edge case where the members list does not contain the caller, which silently locked an owner
     * out of their own admin panel with no explanation.
     */
    const level: ManagementLevel | null =
      me?.management_level ??
      (orgRole ? (orgRole.toLowerCase() as ManagementLevel) : null);
    return {
      currentUserId: authUserId,
      currentUserRole: level,
      organizationId: orgId,
      // Both the roles and the invitations REST surfaces authorise owner OR admin and 403 anyone
      // else (docs/admin-pannel/rbac-roles-api.md). Derived once here so every panel gates on the
      // same predicate — previously "Invite member" was correctly disabled for a plain member while
      // "New role" was always enabled and simply failed with a toast on click.
      canManage: level === 'owner' || level === 'admin',
    };
  }, [members, authUserId, orgRole, orgId]);

  const pendingInvitations = invitations?.filter((invitation) => invitation.status === 'pending').length ?? 0;

  /*
   * The pending-invitation count is PROMOTED to the People tab.
   *
   * It is the panel's only nav badge that means "there is something outstanding here" rather than
   * "here is a total", and nesting Invitations one level down would have hidden a time-sensitive
   * item — an invitation expires. Surfacing it on the parent is the mitigation for that, so an
   * outstanding invite is visible without entering the section.
   *
   * Members and Roles counts move to the People sub-tabs, beside the lists they count.
   */
  const counts: Record<OrgAdminTab, { value: number | null; tone?: 'warn' }> = {
    // No count: Overview summarises several rosters at once, so any single number would be picking
    // one of them arbitrarily.
    overview: { value: null },
    people: {
      value: pendingInvitations || null,
      tone: pendingInvitations ? 'warn' : undefined,
    },
    // No count: the catalog is a short, admin-curated list, not a roster with something outstanding.
    mcp: { value: null },
    billing: { value: null },
    usage: { value: null },
    // No count, and no "unhealthy" badge either. A badge here would need a threshold for what counts
    // as bad, which nothing in the observability contract defines — see `SHOW_AGENTIC` in
    // `overview/UsageSection` for the same trap on a raw ratio.
    observability: { value: null },
  };

  return (
    // `TooltipProvider` is mounted here rather than app-wide: StudioLayout has none, and every row
    // action in this module is an icon button whose name is carried by a tooltip.
    <TooltipProvider delayDuration={250}>
      <div className="oa-route flex h-full min-h-0 flex-col">
        {/* Layout direction is set by a container query in org-admin-tokens.css, not here — it
            depends on the panel's width, which Tailwind's viewport breakpoints cannot see. */}
        <header className="oa-header flex gap-x-6 gap-y-1.5 pb-3 pt-3">
          <div className="min-w-0">
            <h1 className="oa-hero">Admin panel</h1>
            {/*
              The scope line replaces "…for {orgName}".

              A bare org name cannot distinguish the customer's company from one connected Salesforce
              environment, and in this product it is always the latter — with its own roster, roles,
              invitations, billing scope and budgets. `ConnectionScopeLine` adds the environment and
              the instance host, which are what actually tell two connections apart. See its header.
            */}
            <ConnectionScopeLine className="mt-1" />
          </div>

          {/*
            The one number worth carrying at page level: spend against the current budget cycle is
            the thing an admin wants to see without navigating. The other three figures from the old
            four-card stat strip (members / roles / pending invitations) now sit on the tab that owns
            them, where the number and the list it summarises cannot disagree.
          */}


          {/* TODO: Product asked to remove it for now */}
         
          {/* {usage && activeTab !== 'analysis' && (
            <dl className="oa-header-stat shrink-0">
              <dt className="oa-micro">Spend · budget cycle</dt>
              <dd className="mt-0.5 text-[17px] font-semibold tabular-nums">
                {formatCurrencyUsd(usage.total_spend_usd)}
              </dd>
            </dl>
          )} */}
        </header>

        {/*
          Radix Tabs, so the tab/tabpanel relationship, `aria-selected`, arrow-key navigation and
          focus management come from the primitive. The previous bar was four bare `<button>`s with
          an animated underline whose width was hard-coded to `w-16` and whose position was computed
          as a percentage of the bar — so it lined up with a tab only by coincidence, and never at
          all once labels differed in width.
        */}
        <Tabs
          value={location.tab}
          onValueChange={setActiveTab}
          className="flex min-h-0 flex-1 flex-col"
        >
          <TabsList aria-label="Admin sections" className="oa-tablist h-auto shrink-0 justify-start rounded-none bg-transparent p-0">
            {ORG_ADMIN_TABS.map((tab) => {
              const { label, icon: Icon } = TAB_META[tab];
              const count = counts[tab];
              return (
                <TabsTrigger key={tab} value={tab} className="oa-tab rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none">
                  <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  {label}
                  {count.value !== null && (
                    <span className="oa-tab-count" data-tone={count.tone}>
                      {count.value}
                    </span>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/*
            `forceMount` is deliberately NOT used: each panel owns filter/sort/search state, and
            keeping five panels mounted would also keep five sets of queries subscribed. Radix
            unmounts the inactive panels, so switching tabs resets a search box — which is the
            behaviour an admin expects, since the search belongs to the list they can see.

            SINCE OBSERVABILITY ARRIVED THIS IS ALSO A REQUIREMENT, not just a preference. That tab
            was moved here on the condition that its APIs fire only when it is selected, and this is
            the single line that delivers it — the panel does not exist until the tab is active, so
            its query cannot have gone out. Adding `forceMount` here (the usual fix for a lost scroll
            position) fires the observability schema probe and events query on every visit to the
            Admin panel, and changes nothing you can see in a screenshot.
            `qa/observability.mjs` fails three checks if you do; that is how it was verified.
          */}
          {ORG_ADMIN_TABS.map((tab) => (
            <TabsContent
              key={tab}
              value={tab}
              // `flex flex-col` is what lets the panel inside claim the remaining height: without it
              // the panel's own `flex-1` has no flex parent to grow in, so a short roster left the
              // table floating above a screen's worth of empty space.
              // `mt-0` because the primitive's `mt-2` would gap the panel away from the tab bar.
              className="oa-fade-in mt-0 flex min-h-0 flex-1 flex-col focus-visible:outline-none data-[state=inactive]:hidden"
            >
              {tab === 'overview' && <OverviewPanel self={self} onNavigateTab={setActiveTab} />}
              {tab === 'people' && (
                <PeoplePanel
                  self={self}
                  section={location.section}
                  onSelectSection={setPeopleSection}
                  pendingInvitations={pendingInvitations}
                  memberCount={members?.length ?? null}
                  roleCount={roles?.length ?? null}
                />
              )}
              {tab === 'mcp' && <McpServersPanel />}
              {tab === 'billing' && <BillingPanel self={self} />}
              {tab === 'usage' && <UsagePanel self={self} />}
              {/*
                No `self` prop: this tab takes no authority decision. Its data is the org's
                observability event log rather than an RBAC or billing surface, and it carried no
                permission as a page — see `ObservabilityPanel`'s header on why adding one here would
                be inventing a rule rather than honouring one.
              */}
              {tab === 'observability' && <ObservabilityPanel />}
              {/* Rewards returns as a card inside Billing when its endpoints land: a referral reward
                  is a credit against the bill, so it belongs where the bill is — not as a fifth peer
                  competing with Members for a slot. `?tab=rewards` already resolves to Billing. */}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </TooltipProvider>
  );
}
